import React, {Component} from "react";
import {connect} from "react-redux";
import {Box, IconButton, Popover, Tab, Tabs, Tooltip} from '@mui/material';
import Brightness6Icon from '@mui/icons-material/Brightness6';
import ContrastIcon from '@mui/icons-material/Contrast';
import TuneIcon from '@mui/icons-material/Tune';
import {brightnessKey, contrastKey, invertKey} from '../utils/viewerSettingsKeys'
import {Loading, SliderRow} from "../components/components";
import {updateDisplaySetting, updateMapState} from "../../../redux_actions/maps.state.action";
import {addKeyboardListener, isPermitted, removeKeyboardListeners} from "../../../utils/listeners";
import {morphleBaseTheme1} from "../themes";

class DisplaySetting extends Component {
	constructor(props) {
		super(props);
		this.state = this.initState();
	}

	componentDidMount() {
		addKeyboardListener(document, this.keyDown, this.keyUp);
		// Listen for RGB Curves overlay open/close events
		this.rgbCurvesOpenListener = () => {
			this.setState({ rgbCurvesOpen: true });
		};
		this.rgbCurvesCloseListener = () => {
			this.setState({ rgbCurvesOpen: false });
		};
		window.addEventListener('rgbCurvesOverlayOpened', this.rgbCurvesOpenListener);
		window.addEventListener('rgbCurvesOverlayClosed', this.rgbCurvesCloseListener);
	}

	componentWillUnmount() {
		removeKeyboardListeners(document, this.keyDown);
		// Remove RGB Curves event listeners
		if (this.rgbCurvesOpenListener) {
			window.removeEventListener('rgbCurvesOverlayOpened', this.rgbCurvesOpenListener);
		}
		if (this.rgbCurvesCloseListener) {
			window.removeEventListener('rgbCurvesOverlayClosed', this.rgbCurvesCloseListener);
		}
	}

	keyDown = (event) => {
		if (!isPermitted(event)) return;
		let key, value;
		switch (event.code) {
			case 'KeyB':
				key = brightnessKey.id;
				value = this.props.displaySetting[key] + 15;
				break;
			case 'KeyC':
				key = contrastKey.id;
				value = this.props.displaySetting[key] + 15;
				break;
			case 'KeyI':
				key = invertKey.id;
				value = invertKey.max;
				break;
			default:
				break;
		}
		key && this.props.dispatch(updateDisplaySetting(this.props.activeMapId, {
			id: this.props.displaySetting.id,
			[key]: value
		}));
	}

	keyUp = (event) => {
		if (!isPermitted(event)) return;
		let key, value;
		switch (event.code) {
			case 'KeyB':
				key = brightnessKey.id;
				value = this.props.displaySetting[key] - 15;
				break;
			case 'KeyC':
				key = contrastKey.id;
				value = this.props.displaySetting[key] - 15;
				break;
			case 'KeyI':
				key = invertKey.id;
				value = invertKey.min;
				break;
			default:
				break;
		}
		key && this.props.dispatch(updateDisplaySetting(this.props.activeMapId, {
			id: this.props.displaySetting.id,
			[key]: value,
		}));
	}

	initState = () => {
		return {
			anchorEl: null,
			setting: false,
			rgbCurvesOpen: false
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.activeMapId !== this.props.activeMapId && !this.props.displaySetting) {
			this.setState(this.initState());
			this.props.dispatch(updateMapState(this.props.activeMapId, {displaySetting: this.props.slide.display_setting}));
		}
	}

	handleTabChange = (event, setting) =>
		this.setState({
			anchorEl: event.currentTarget,
			setting,
		});

	handleClose = () => {
		this.setState({
			anchorEl: null,
			setting: false
		});
	}

	render() {
		if (!this.props.displaySetting)
			return <Loading/>;

		return (
			<>
				<Tabs value={this.state.setting} onChange={this.handleTabChange}>
					<Tooltip title={"Brightness"} placement={"bottom"} value={brightnessKey} className='topBarBrightness-forTour'>
						<Tab icon={<Brightness6Icon/>} value={brightnessKey} sx={{marginRight: -1}}/>
					</Tooltip>
					<Tooltip title={"Contrast"} placement={"bottom"} value={contrastKey} className='topBarContrast-forTour'>
						<Tab icon={<ContrastIcon/>} value={contrastKey}/>
					</Tooltip>
				</Tabs>
				<Tooltip title={"RGB Curves"} placement={"bottom"}>
					<IconButton
						size="small"
						onClick={() => {
							// Toggle overlay: close if open, open if closed
							if (this.state.rgbCurvesOpen) {
								window.dispatchEvent(new CustomEvent('closeRGBCurvesOverlay'));
							} else {
								window.dispatchEvent(new CustomEvent('openRGBCurvesOverlay'));
							}
						}}
						sx={{
							marginLeft: 0.5,
							padding: '8px',
							// Match selected tab styling when overlay is open
							backgroundColor: this.state.rgbCurvesOpen ? morphleBaseTheme1.palette.primary.main : 'transparent',
							color: this.state.rgbCurvesOpen ? morphleBaseTheme1.palette.secondary.main : morphleBaseTheme1.palette.headBarIcon.primary,
							'&:hover': {
								backgroundColor: this.state.rgbCurvesOpen ? morphleBaseTheme1.palette.primary.main : 'rgba(255, 255, 255, 0.1)',
							}
						}}
					>
						<TuneIcon/>
					</IconButton>
				</Tooltip>
				<Popover open={!!this.state.anchorEl} anchorEl={this.state.anchorEl} onClose={this.handleClose}
				         anchorOrigin={{vertical: 'bottom', horizontal: 'left',}} sx={{width: 300}}>
					<Box width={200} paddingY={1} paddingX={2}>
						<SliderRow value={this.props.displaySetting[this.state.setting.id]} hideInput={true}
								   min={this.state.setting.min} max={this.state.setting.max}
								   update={value => this.props.dispatch(updateDisplaySetting(this.props.activeMapId,
									   {
										   id: this.props.displaySetting.id,
										   [this.state.setting.id]: value,
									   }))}
								   color={"sliderDefaultColor"}
						/>
					</Box>
				</Popover>
			</>
		)
	}
}

const mapStateToProps = (state) => {
	let {activeMapId} = state.gammaStateReducer;
	let mapState = state.mapsStateReducer[activeMapId];
	let {slide, displaySetting} = mapState;
	return {activeMapId, mapState, slide, displaySetting};
}

export default connect(mapStateToProps)(DisplaySetting)
